import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { IPopupMenuConfig } from "libraries/component/popup-menu/popup-menu.component";
import { Yii } from "libraries/interfaces/Yii";
import {
    MAIN_LABELS
} from "libraries/component/booking-information/components/booking-main-informations/config/wording";
import {
    IGetBookingsAirResponse,
    IGetBookingsCommonResponse,
    IGetBookingsStayResponse,
    TGetBookingResponse
} from "libraries/service/resources/booking/interfaces/IGetBookings";
import { ConfirmDialogComponent } from "libraries/component/dialogs/confirm-dialog/confirm-dialog.component";
import { filter } from "rxjs/operators";
import { PermissionResourceService } from "libraries/service/resources/permission/permissionResource.service";
import { forkJoin } from "rxjs";
import { ErrorTracingService, TraceSeverity } from "libraries/service/error-tracing/error-tracing.service";
import { TextExportDialogComponent } from 'libraries/component/dialogs/text-export-dialog/text-export-dialog.component';
import { PlainTextService } from 'libraries/service/plainText/plainText.service';
import { BookingType } from 'libraries/service/resources/booking/interfaces/get-bookings.enum';
import { MatDialog, MatDialogRef } from "@angular/material/dialog";
import { EAirBookingPermissions } from "libraries/component/booking-information/e-air-booking-permissions.enum";
import { BookingAirToolsService } from "libraries/component/booking-information/services/booking-air-tools.service";
import {
    DIALOG_CANCEL_WORDING,
    DIALOG_ISSUE_WORDING,
    DIALOG_QUALITY_CHECK_WORDING,
    DIALOG_UPDATE_WORDING,
    DIALOG_VOID_WORDING,
    DIALOG_VOUCHER_WORDING, IDialogLabels
} from "libraries/component/booking-information/components/booking-main-informations/components/booking-actions-menu/booking-actions-menu-wording";

enum EBookingAction {
    update = "update",
    getVoucher = "getVoucher",
    void = "void",
    cancel = "cancel",
    issue = "issue",
    qualityCheck = "qualityCheck",
    exportTextFormat = "exportTextFormat",
    modifyItinerary = "modifyItinerary",
    addServices = "addServices"
}

@Component({
    selector: 'booking-actions-menu',
    template: `
        <popup-menu *ngIf="this.myMenuConfig?.items?.length"
                    [menuConfig]="myMenuConfig"
                    [disabled]="disableBookingActions"
                    (onSelect)="dispatchProcessing($event)"></popup-menu>
    `,
    styles: []
})
export class BookingActionsMenuComponent implements OnInit {

    public myMenuConfig: IPopupMenuConfig;

    @Input()
    public bookingData: TGetBookingResponse;

    @Input()
    public showBookingActions: boolean = true;

    @Input()
    public disableBookingActions: boolean = false;

    @Output()
    public readonly onQualityCheck: EventEmitter<void> = new EventEmitter();

    @Output()
    public readonly onIssue: EventEmitter<void> = new EventEmitter();

    @Output()
    public readonly onCancel: EventEmitter<void> = new EventEmitter();

    @Output()
    public readonly onVoid: EventEmitter<void> = new EventEmitter();

    @Output()
    public readonly onUpdate: EventEmitter<void> = new EventEmitter();

    @Output()
    public readonly onGetVoucher: EventEmitter<void> = new EventEmitter();

    @Output()
    public readonly onModifyItinerary: EventEmitter<void> = new EventEmitter();

    @Output()
    public readonly onAddServices: EventEmitter<void> = new EventEmitter();

    @Output()
    public readonly onAddServicesAbilityComputed: EventEmitter<boolean> = new EventEmitter<boolean>();

    constructor(
        private readonly dialog: MatDialog,
        private readonly permissionService: PermissionResourceService,
        private readonly errorTracingService: ErrorTracingService,
        private readonly plainTextService: PlainTextService,
        private readonly bookingAirToolsService: BookingAirToolsService
    ) {
    }

    public ngOnInit(): void {
        // Build empty menu config
        this.myMenuConfig = {
            buttonLabel: Yii.t('wui-shared-js', 'Actions'),
            items: []
        };

        // Populate menu items
        forkJoin([
            this.permissionService.hasPermissionWithRestriction(EAirBookingPermissions.SHARED_AIR_BOOKING_QUALITY_CHECK, this.bookingData),
            this.permissionService.hasPermissionWithRestriction(EAirBookingPermissions.SHARED_AIR_BOOKING_ISSUE, this.bookingData),
            this.permissionService.hasPermissionWithRestriction(EAirBookingPermissions.SHARED_AIR_BOOKING_CANCEL, this.bookingData),
            this.permissionService.hasPermissionWithRestriction(EAirBookingPermissions.SHARED_AIR_BOOKING_VOID, this.bookingData),
            this.permissionService.hasPermission(EAirBookingPermissions.SHARED_STAY_VOUCHER),
            this.permissionService.hasPermission(EAirBookingPermissions.SHARED_VEHICLERENTAL_VOUCHER),
            this.permissionService.hasPermissionWithRestriction(EAirBookingPermissions.SHARED_AIR_BOOKING_EXPORT_TEXT, this.bookingData),
            this.permissionService.hasPermissionWithRestriction(EAirBookingPermissions.SHARED_AIR_BOOKING_SEGMENT_MODIFY, this.bookingData),
            this.permissionService.hasPermissionWithRestriction(EAirBookingPermissions.SHARED_BOOKING_SYNCHRONIZE, this.bookingData),
            this.permissionService.hasPermissionWithRestriction(EAirBookingPermissions.SHARED_AIR_BOOKING_SERVICES_ADD, this.bookingData),
        ]).subscribe({
            next: this.addMenuActions.bind(this)
        });
    }

    /**
     * Dispatches processing to apropriate method.
     * @param input name of processing type
     */
    public dispatchProcessing(input: string): void {
        const $event: EBookingAction = input as EBookingAction;
        switch ($event) {
            case EBookingAction.qualityCheck:
                this.showConfirmDialog(DIALOG_QUALITY_CHECK_WORDING, (): void => {
                    this.onQualityCheck.emit();
                });
                break;
            case EBookingAction.issue:
                this.showConfirmDialog(DIALOG_ISSUE_WORDING, (): void => {
                    this.onIssue.emit();
                });
                break;
            case EBookingAction.cancel:
                this.showConfirmDialog(DIALOG_CANCEL_WORDING, (): void => {
                    this.onCancel.emit();
                });
                break;
            case EBookingAction.void:
                this.showConfirmDialog(DIALOG_VOID_WORDING, (): void => {
                    this.onVoid.emit();
                });
                break;
            case EBookingAction.update:
                this.showConfirmDialog(DIALOG_UPDATE_WORDING, (): void => {
                    this.onUpdate.emit();
                });
                break;
            case EBookingAction.getVoucher:
                this.showConfirmDialog(DIALOG_VOUCHER_WORDING, (): void => {
                    this.onGetVoucher.emit();
                });
                break;
            case EBookingAction.exportTextFormat:
                this.showExportDialog();
                break;
            case EBookingAction.modifyItinerary:
                this.onModifyItinerary.emit();
                break;
            case EBookingAction.addServices:
                this.onAddServices.emit();
                break;
            default:
                this.errorTracingService.captureMessage(`${$event} should be one of EBookingAction`, TraceSeverity.Warning);
                break;
        }
    }

    private addMenuActions([hasQualityCheckPermission,
                               hasIssuePermission,
                               hasCancelPermission,
                               hasVoidPermission,
                               hasVoucherPermission,
                               hasVehicleVoucherPermission,
                               hasExportPermission,
                               hasModifySegmentPermission,
                               hasSynchronizePermission,
                               hasAddServicesPermission
                           ]:
                               [IGetBookingsCommonResponse[], boolean, boolean, boolean, boolean, boolean, boolean, boolean, boolean, boolean]): void {
        if (!this.bookingData) {
            return;
        }

        if (this.showBookingActions) {
            this.initQualityCheckMenuItem(hasQualityCheckPermission);

            if (hasIssuePermission && this.bookingData.computed?.issuable) {
                // Push issue item
                this.myMenuConfig.items.push({label: MAIN_LABELS.issueBooking, actionName: EBookingAction.issue});
            }
            if (hasCancelPermission && this.bookingData.computed?.cancellable) {
                // Push cancel item
                this.myMenuConfig.items.push({label: MAIN_LABELS.cancelBooking, actionName: EBookingAction.cancel});
            }
            if (hasVoidPermission && this.bookingData.computed?.voidable) {
                // Push void item
                this.myMenuConfig.items.push({label: MAIN_LABELS.voidBooking, actionName: EBookingAction.void});
            }
            // this is a quick&dirty check for APITUDE voucher */
            this.initGetVoucherMenuItem(hasVoucherPermission);

            if (hasSynchronizePermission) {
                // Push void item
                this.myMenuConfig.items.push({label: MAIN_LABELS.update, actionName: EBookingAction.update});
            }


            if (hasVehicleVoucherPermission && this.bookingData.type && this.bookingData.type.includes(BookingType.vehicleRental)) {
                this.myMenuConfig.items.push({
                    label: MAIN_LABELS.getBookingVoucher,
                    actionName: EBookingAction.getVoucher
                });
            }
        }

        if (hasExportPermission && this.bookingData.type && this.bookingData.type.includes(BookingType.air)) {
            this.myMenuConfig.items.push({
                label: MAIN_LABELS.getItineraryInText,
                actionName: EBookingAction.exportTextFormat
            });
        }

        const isOneSectorLowCost: boolean = this.bookingAirToolsService.isOneSectorLowCost(this.bookingData);

        if (hasModifySegmentPermission && this.bookingData.type && this.bookingData.type.includes(BookingType.air) && !isOneSectorLowCost &&
            this.bookingData.issueStatus === 'notIssued') {
            this.myMenuConfig.items.push({
                label: MAIN_LABELS.modifyItinerary,
                actionName: EBookingAction.modifyItinerary
            });
        }

        if (this.bookingAirToolsService.canAddServices(hasAddServicesPermission, this.bookingData, isOneSectorLowCost)) {
            this.myMenuConfig.items.push({
                label: MAIN_LABELS.addServices,
                actionName: EBookingAction.addServices
            });
        }

        // if (hasAddServicesPermission && this.bookingData.type && this.bookingData.type.includes(BookingType.air) &&
        //     !isOneSectorLowCost &&
        //     this.bookingData.xType && this.bookingData.xType === BookingXType.individual &&
        //     !(
        //         (this.bookingData.host && (this.bookingData.host === "TRAVELFUSION" || this.bookingData.host === "TRAVELPORT")) &&
        //         this.bookingData.xStatus && this.bookingData.xStatus === BookingXStatus.Confirm && this.bookingData.begin && moment(this.bookingData.begin, 'YYYY-MM-DD') > moment()
        //     )
        //     ) {
        //     this.myMenuConfig.items.push({
        //         label: MAIN_LABELS.addServices,
        //         actionName: EBookingAction.addServices
        //     });
        //     this.onAddServicesAbilityComputed.emit(true);
        // }else{
        //     this.onAddServicesAbilityComputed.emit(false);
        // }
    }

    private initQualityCheckMenuItem(hasQualityCheckPermission: IGetBookingsCommonResponse[]): void {
        if (hasQualityCheckPermission && this.bookingData?.type?.includes(BookingType.air)) {
            // Push qualityCheck item
            this.myMenuConfig.items.push({
                label: MAIN_LABELS.qualityCheckBooking,
                actionName: EBookingAction.qualityCheck
            });
        }
    }

    private initGetVoucherMenuItem(hasVoucherPermission: boolean): void {
        if (hasVoucherPermission && this.bookingData?.type) {
            if (
                this.bookingData.type.includes(BookingType.stay) &&
                (this.bookingData as IGetBookingsStayResponse | IGetBookingsAirResponse).sector
            ) {
                // Push getVoucher item
                this.myMenuConfig.items.push({
                    label: MAIN_LABELS.getBookingVoucher,
                    actionName: EBookingAction.getVoucher
                });
            }
        }

    }

    /**
     * Shows a confirm dialog.
     * @param labels labels displayed
     * @param next what to do when the confirm dialog is validated
     */
    private showConfirmDialog(labels: IDialogLabels, next: () => void): void {
        const confirmDialog: MatDialogRef<ConfirmDialogComponent> = this.dialog.open(ConfirmDialogComponent, {
            width: '650px',
            data: labels,
        });

        confirmDialog
            .afterClosed()
            .pipe(
                filter((value: string): boolean => value === 'process'),
            ).subscribe(next);
    }

    private showExportDialog(): void {
        this.dialog.open(TextExportDialogComponent, {
            minWidth: '50%',
            data: this.plainTextService.generateBookingExport(this.bookingData as IGetBookingsAirResponse),
        });
    }
}
